import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader } from '@xxtract/ui';
import { formatDatum, formatGetal, isGetal, isTelling } from '../format';
import { CategorieStaven } from './CategorieStaven';
import { GeenMeting } from './MeasurementValue';
import { RegressieBlok } from './RegressieBlok';
import type { Kerngetal, Weekrapport } from '../work-quality.api';

export interface WeekReportCardProps {
  rapport: Weekrapport | null;
  /**
   * Het runmoment van het rapport waaruit de veldtabel hierboven komt — spec-weekrapport-dashboard,
   * AC6.
   *
   * `/medewerkers` en `/weekrapport` zijn twee aanroepen met twee eigen opvragingen; een rapport dat
   * tussen die twee door wordt geschreven zet cijfers uit twee weken op één scherm. De dienst kan
   * dat niet voorkomen, maar wel zichtbaar maken — dezelfde beweging als `instellingVersie` een laag
   * lager. Ontbreekt de datum, dan zegt het scherm niets: onbekend is geen ongelijkheid.
   */
  topVeldenBerekenDatum?: string;
}

/**
 * Of dit verschil goed of slecht nieuws is.
 *
 * Geen rekenwerk maar een aflezing: `hogerIsBeter` komt uit het antwoord, en het verschil ook. Het
 * scherm hoeft dus niet te weten wát een kerngetal meet om het woord te kiezen — precies wat er
 * misgaat als iemand hallucinatie vergeet, waar een daling juist goed is. De pijl volgt los daarvan
 * het getal (EXPERIENCE.md, afwijking 8).
 */
function richtingVan(rij: Kerngetal): 'beter' | 'slechter' | 'gelijk' | 'onbekend' {
  // `isGetal` en niet `isTelling`: een verschil mag negatief zijn, een gemeten waarde niet.
  if (!isGetal(rij.verschilPp)) return 'onbekend';
  if (rij.verschilPp === 0) return 'gelijk';
  return rij.verschilPp > 0 === rij.hogerIsBeter ? 'beter' : 'slechter';
}

/**
 * Stap 4 van de leesvolgorde: het wekelijkse rapport als beeld, met de tekst er ongewijzigd onder.
 *
 * De kaart bouwt de drie blokken niet zelf — de vier kerngetallen, de negen categoriestaven en de
 * regressies staan elk in hun eigen component. Wat hier gebeurt is de koppeling, en het onderscheid
 * dat overal in deze module geldt: een blok dat de meetdienst niet stuurt verdwijnt, en een blok dat
 * zij leeg stuurt zegt dat het leeg is.
 *
 * `rapportMarkdown` gaat er byte voor byte in. Er is geen bewerking en geen herschikking: het
 * rapport bestaat, is leesbaar, en het opnieuw ontwerpen zou werk zijn zonder opbrengst. De kop
 * draagt de periode en het moment van de run, zodat zichtbaar is dat dit rapport zijn eigen schema
 * volgt en niet per se met de gemeten week samenvalt.
 *
 * De tekst staat in een `<pre>` en niet als opgemaakte markdown. Er is in dit portaal geen
 * markdown-weergave, en er een afhankelijkheid voor binnenhalen is een keuze over de hele
 * toepassing — inclusief wat er met de HTML in een document van een andere dienst gebeurt — en geen
 * schermbesluit. Ongewijzigd tonen is wat het acceptatiecriterium vraagt, en er gaat op deze manier
 * geen letter verloren. Zie de trace.
 */
export function WeekReportCard({ rapport, topVeldenBerekenDatum }: WeekReportCardProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? i18n.language;

  // Een lege tekst is geen rapport maar een kapot antwoord: een leeg blok zou lezen als "het
  // rapport zei deze week niets", en dat is precies de stilte die het contract uitsluit.
  const ruw = typeof rapport?.rapportMarkdown === 'string' ? rapport.rapportMarkdown : null;
  const tekst = ruw !== null && ruw.trim() !== '' ? ruw : null;
  const berekenDatum = typeof rapport?.berekenDatum === 'string' ? rapport.berekenDatum : null;
  const week = rapport?.afbakening?.weekKey;

  // Afwezig is niet leeg. Een oudere meetdienst stuurt het blok helemaal niet; dan staat de tekst
  // uitgeklapt, want die is dan alles wat er is.
  const kerngetallen = Array.isArray(rapport?.kerngetallen) ? rapport.kerngetallen : null;
  // Een rapport mét tekst maar zonder kerngetallen komt van vóór spec-weekrapport-dashboard: de
  // oudere meetmethode. Zonder tekst is het geen rapport, en zegt de kaart dat al.
  const oudereMeetmethode = rapport !== null && rapport.reden === undefined && tekst !== null && kerngetallen === null;
  const heeftCijfers = kerngetallen !== null && kerngetallen.length > 0;

  // Twee blokken die op dezelfde manier kunnen ontbreken, en om dezelfde reden apart worden
  // gehouden: afwezig betekent "deze meetdienst stuurt ze niet", een lege lijst betekent "het
  // rapport had er geen". Bij de regressies is dat tweede een echt antwoord en zegt het scherm het
  // met zoveel woorden; bij de categorieën is een lege lijst niets om te tonen.
  const categorieen = Array.isArray(rapport?.categorieen) ? rapport.categorieen : null;
  const regressies = Array.isArray(rapport?.regressies) ? rapport.regressies : null;
  const heeftBlokken = (categorieen?.length ?? 0) > 0 || regressies !== null;

  // Twee aanroepen kunnen twee rapporten opleveren. Onbekend is geen ongelijkheid: zonder de andere
  // datum zwijgt het scherm.
  const uitEenAnderRapport =
    typeof topVeldenBerekenDatum === 'string' &&
    berekenDatum !== null &&
    topVeldenBerekenDatum !== berekenDatum;

  return (
    <section data-werkkwaliteit-sectie="weekrapport">
      <Card>
        <CardHeader className="pb-3">
          <h2
            className="text-[13px] font-semibold text-[var(--foreground-heading)]"
            data-testid="werkkwaliteit-weekrapport-kop"
          >
            {week === undefined
              ? t('qa.alle.weekrapportKop')
              : berekenDatum === null
                ? // De periode staat er ook zonder runmoment: die is bekend, en hem weglaten omdat
                  // het andere ontbreekt maakt de kop minder waar dan hij kan zijn.
                  t('qa.alle.weekrapportKopMetWeek', { week })
                : t('qa.alle.weekrapportKopMetRun', { week, datum: formatDatum(berekenDatum, locale) })}
          </h2>
        </CardHeader>
        <CardContent>
          {uitEenAnderRapport ? (
            <p
              className="mb-3 rounded-[6px] border border-[var(--warning)] bg-[var(--warning-light)] px-3 py-2 text-[12px] text-[var(--warning-text)]"
              data-testid="werkkwaliteit-weekrapport-ongelijk"
            >
              {t('qa.alle.weekrapportOngelijk')}
            </p>
          ) : null}

          {oudereMeetmethode ? (
            <p
              className="mb-3 rounded-[6px] bg-[var(--info-light)] px-3 py-2 text-sm text-[var(--info-text)]"
              data-testid="werkkwaliteit-ai-oudere-meetmethode"
            >
              {t('qa.eigen.weekOudeMeetmethode')}
            </p>
          ) : null}

          {heeftCijfers ? (
            // Op 1280 px vier naast elkaar, op een tablet twee bij twee, op de telefoon onder
            // elkaar (DESIGN.md, AI per breedte).
            <dl
              className="mb-4 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4"
              data-testid="werkkwaliteit-kerngetallen"
            >
              {kerngetallen.map((rij) => (
                <Kerngetalvlak key={rij.sleutel} locale={locale} rij={rij} />
              ))}
            </dl>
          ) : null}

          {categorieen !== null ? <CategorieStaven categorieen={categorieen} /> : null}
          {regressies !== null ? (
            <RegressieBlok regressies={regressies} veldnamen={rapport?.veldnamen} />
          ) : null}

          {tekst === null ? (
            <p className="text-[13px] text-[var(--foreground)]" data-testid="werkkwaliteit-geen-weekrapport">
              {ruw === null ? t('qa.alle.weekrapportGeen') : t('qa.alle.weekrapportLeeg')}
            </p>
          ) : (
            // Ingeklapt zodra er cijfers boven staan, en anders open: de tekst is dan alles wat er
            // is. Er gaat geen letter verloren — hij staat er nog, alleen niet meer als muur.
            <details
              data-testid="werkkwaliteit-weekrapport-vouw"
              open={!heeftCijfers && !heeftBlokken}
            >
              <summary className="cursor-pointer text-[12px] font-medium text-[var(--foreground-muted)]">
                {t('qa.alle.weekrapportTekstVouw')}
              </summary>
              <pre
                className="mt-2 overflow-x-auto whitespace-pre-wrap break-words font-sans text-[13px] text-[var(--foreground)]"
                data-testid="werkkwaliteit-weekrapport-tekst"
              >
                {tekst}
              </pre>
            </details>
          )}
        </CardContent>
      </Card>
    </section>
  );
}

/**
 * Eén kerngetal: de waarde, wat er sinds vorige week mee gebeurde, en wat het meet.
 *
 * De pijl volgt het getal (▲ hoger, ▼ lager), het woord en de kleur volgen de kwaliteit: "beter"
 * groen, "slechter" amber, nooit rood — rood is in deze module een storing (DESIGN.md). Een cijfer
 * zonder meting houdt zijn vak en zegt dat er niets gemeten is — geen nul, geen streepje.
 */
function Kerngetalvlak({ locale, rij }: { locale: string; rij: Kerngetal }) {
  const { t } = useTranslation();
  const richting = richtingVan(rij);
  const kleur =
    richting === 'beter'
      ? 'text-[var(--success-text)]'
      : richting === 'slechter'
        ? 'text-[var(--warning-text)]'
        : 'text-[var(--foreground-muted)]';
  const verschil = Math.abs(rij.verschilPp ?? 0);

  return (
    <div
      className="min-w-0 rounded-lg border border-[var(--border-soft)] bg-[var(--background)] p-3"
      data-richting={richting}
      data-testid={`werkkwaliteit-kerngetal-${rij.sleutel}`}
    >
      <dt className="text-xs font-semibold text-[var(--foreground-muted)]">{t(`qa.ai.kerngetal.${rij.sleutel}`)}</dt>
      <dd className="mt-1">
        {isTelling(rij.waarde) ? (
          // De dienst stuurt het percentage al met één decimaal. Hier wordt alleen nog de
          // landsnotatie toegepast — geen rekenwerk (AD-19).
          <span className="block text-[1.429rem] font-bold leading-[1.2] text-[var(--foreground)] tabular-nums">
            {formatGetal(rij.waarde, locale)}%
          </span>
        ) : (
          <GeenMeting label={t('qa.alle.kerngetalNietGemeten')} />
        )}
        <span className={`mt-1 block text-xs ${kleur}`} data-werkkwaliteit-verandering={richting}>
          {richting === 'onbekend' ? (
            t('qa.ai.geenVergelijking')
          ) : richting === 'gelijk' ? (
            t('qa.ai.gelijk')
          ) : (
            <>
              <span aria-hidden="true">{t((rij.verschilPp ?? 0) > 0 ? 'qa.ai.pijlOmhoog' : 'qa.ai.pijlOmlaag')}</span>{' '}
              {t(richting === 'beter' ? 'qa.ai.beter' : 'qa.ai.slechter', {
                // Het teken staat al in het woord; het zou er anders twee keer staan.
                count: verschil,
                verschil: formatGetal(verschil, locale),
              })}
            </>
          )}
        </span>
        {rij.hogerIsBeter === false ? (
          <span className="mt-0.5 block text-xs font-medium text-[var(--foreground)]">{t('qa.ai.lagerIsBeter')}</span>
        ) : null}
        <span className="mt-1 block text-xs text-[var(--foreground-muted)]">{t(`qa.ai.uitleg.${rij.sleutel}`)}</span>
      </dd>
    </div>
  );
}
